<template lang="pug">
.gallery-tab
  .gallery-block.images(v-if="allImages.length")
    h5.block-header {{ __('Images') }}
    .thumbnail-grid
      .img-thumb(v-for="(image, i) in displayedImages" @click="selectedIndex = i; showImageGallery = true")
        StaticImage(:src="image.src" :alt="__('Gym image {n}', { n: String(i + 1) })" :width="84" :height="84")
    RogueButton.more-less(
      v-if="allImages.length > moreImagesCount"
      @click="showAllImages = !showAllImages"
      color="black-outline"
      width="full"
    )
      | {{ showAllImages ? __('See Less') : __('See More') }}
      Icon.more-less-icon(:name="showAllImages ? 'arrow-up' : 'arrow-down'" fill="black")
      Icon.more-less-icon-hover(:name="showAllImages ? 'arrow-up' : 'arrow-down'" fill="white")
  .gallery-block.videos(v-if="allVideos.length")
    h5.block-header {{ __('Videos') }}
    .video-thumbnails
      .video-thumb(v-for="(video, i) in displayedVideos" @click="selectedIndex = i; showVideoGallery = true")
        video(:src="video.cloudinaryUrl")
        Icon.play-icon(name="play" fill="white")
    RogueButton.more-less(
      v-if="allVideos.length > moreVideosCount"
      @click="showAllVideos = !showAllVideos"
      color="black-outline"
      width="full"
    )
      | {{ showAllVideos ? __('See Less') : __('See More') }}
      Icon.more-less-icon(:name="showAllVideos ? 'arrow-up' : 'arrow-down'" fill="black")
      Icon.more-less-icon-hover(:name="showAllVideos ? 'arrow-up' : 'arrow-down'" fill="white")
  CarouselModal(v-if="showImageGallery" @close="showImageGallery = false" :value="selectedIndex")
    DynamicMedia.zoom-horiz-img(
      v-for="image in allImages"
      :key="image.src"
      :config="{ src: image.src, autoHeight: true }"
      :contain="true"
    )
  CarouselModal(v-if="showVideoGallery" @close="showVideoGallery = false" :value="selectedIndex")
    DynamicMedia.zoom-horiz-img(
      v-for="video in allVideos"
      :key="video.src"
      :config="{ src: video.src, autoHeight: true, enableVideoControls: true }"
      :contain="true"
    )
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { getCloudinaryUrl } from 'sisu/js/helpers'
import type { IGym, IGymMedia } from '@rogue/universal'

const props = defineProps<{
  gym: IGym
}>()

const moreImagesCount = 8
const moreVideosCount = 1
const showAllImages = ref(false)
const showAllVideos = ref(false)
const showImageGallery = ref(false)
const showVideoGallery = ref(false)
const selectedIndex = ref(0)

const allImages = ref<IGymMedia[]>((props.gym.media || []).filter(m => m.type === 'image'))
const allVideos = ref<(IGymMedia & { cloudinaryUrl?: string })[]>((props.gym.media || []).filter(m => m.type === 'video'))

allVideos.value.forEach(video => {
  video.cloudinaryUrl = getCloudinaryUrl(video.src)
})

const displayedImages = computed(() => {
  if (allImages.value.length > moreImagesCount && !showAllImages.value) {
    return allImages.value.slice(0, moreImagesCount)
  }

  return allImages.value
})

const displayedVideos = computed(() => {
  if (allVideos.value.length > moreVideosCount && !showAllVideos.value) {
    return allVideos.value.slice(0, moreVideosCount)
  }

  return allVideos.value
})

</script>

<style lang="scss" scoped>
.gallery-block {
  border-bottom: 1px solid $color-gray;
  padding: $space-s;

  &:last-of-type {
    border-bottom: none;
  }
}

.block-header {
  margin-bottom: $space-xs;
  text-transform: uppercase;
}

.more-less {
  margin-top: $space-s;

  .button-content {
    align-items: center;
  }

  .more-less-icon,
  .more-less-icon-hover {
    height: $space-xs;
    margin-left: $space-xxs;
    width: $space-xs;
  }

  .more-less-icon {
    display: inline-block;
  }

  .more-less-icon-hover {
    display: none;
  }

  &:hover {
    .more-less-icon-hover {
      display: inline-block;
    }

    .more-less-icon {
      display: none;
    }
  }
}

.thumbnail-grid {
  display: flex;
  flex-wrap: wrap;
  gap: $space-s;
}

.img-thumb {
  width: 84px;
  height: 84px;

  &:hover {
    cursor: pointer;
  }
}

.zoom-horiz-img {
  max-width: 100%;
  max-height: 100%;
  height: 100%;

  :deep(.rg-img) {
    background-color: transparent;
  }
}

.video-thumbnails {
  display: flex;
  flex-direction: column;
  gap: $space-s;
}

.video-thumb {
  aspect-ratio: 16 / 9;
  cursor: pointer;
  height: fit-content;
  position: relative;
  width: 100%;

  >video {
    height: 100%;
    pointer-events: none;
    width: 100%;
  }

  .play-icon {
    height: 50px;
    left: 50%;
    position: absolute;
    top: 50%;
    transform: translate(-50%, -50%);
    width: 50px;
  }
}
</style>
